/** Toast — design spec §7. */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Check, TriangleAlert, X } from "lucide-react";

import { cn } from "@/lib/cn";

export type ToastTone = "success" | "error";

/**
 * §7 — two positions, and they mean different things: load failures appear
 * under Telegram's header (top), confirmations of something the user just did
 * appear above the tab bar (bottom), next to where the action happened.
 */
export type ToastPosition = "top" | "bottom";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastOptions {
  tone?: ToastTone;
  position?: ToastPosition;
  action?: ToastAction;
  /** §7 — 3–4s. */
  durationMs?: number;
  /** Show an ✕ instead of an action. */
  dismissible?: boolean;
}

interface ToastItem extends Required<Pick<ToastOptions, "tone" | "position">> {
  id: number;
  message: string;
  action?: ToastAction;
  dismissible: boolean;
}

interface ToastApi {
  show: (message: string, options?: ToastOptions) => void;
  success: (message: string, options?: Omit<ToastOptions, "tone">) => void;
  error: (message: string, options?: Omit<ToastOptions, "tone">) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, number>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      timers.current.delete(id);
    }
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback(
    (message: string, options: ToastOptions = {}) => {
      const {
        tone = "success",
        position = "bottom",
        action,
        durationMs = 3500,
        dismissible = false,
      } = options;
      const id = nextId.current++;
      setItems((prev) => [...prev, { id, message, tone, position, action, dismissible }]);
      timers.current.set(
        id,
        window.setTimeout(() => dismiss(id), durationMs),
      );
    },
    [dismiss],
  );

  // Never leave a timer running against an unmounted provider.
  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach((t) => window.clearTimeout(t));
      pending.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (m, o) => show(m, { ...o, tone: "success" }),
      // Load failures default to the top slot, per §7 / §9.
      error: (m, o) => show(m, { position: "top", ...o, tone: "error" }),
    }),
    [show],
  );

  const render = (position: ToastPosition) => {
    const slice = items.filter((t) => t.position === position);
    if (slice.length === 0) return null;
    return (
      <div
        className={cn(
          "pointer-events-none fixed inset-x-0 z-toast flex flex-col items-center gap-2 px-4",
          // §7 — clear of Telegram's 56px header, or of the 88px tab bar.
          position === "top" ? "top-[64px]" : "bottom-[96px]",
        )}
        role="region"
        aria-live={position === "top" ? "assertive" : "polite"}
      >
        {slice.map((t) => (
          <ToastRow key={t.id} item={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    );
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <>
          {render("top")}
          {render("bottom")}
        </>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

/**
 * Success is a quiet `surface` plate with a hairline and a check; an error is
 * the strong plate (text colour, `bg` glyphs) with an alert icon. Not red:
 * that colour is the «Препятствие» status only (Spec 020 D2).
 */
function ToastRow({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const isError = item.tone === "error";
  return (
    <div
      className={cn(
        "pointer-events-auto flex w-full max-w-app animate-toast-in items-center gap-2 rounded-card px-3 py-2.5 text-body font-medium",
        isError ? "bg-p-text text-p-bg" : "border border-p-border bg-p-surface text-p-text",
      )}
    >
      {isError ? (
        <TriangleAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
      ) : (
        <Check className="h-4 w-4 shrink-0 text-p-done" aria-hidden="true" />
      )}
      <span className="min-w-0 flex-1">{item.message}</span>
      {item.action && (
        <button
          type="button"
          onClick={() => {
            item.action?.onClick();
            onDismiss();
          }}
          className={cn(
            "hit-44 shrink-0 font-medium",
            isError ? "text-p-bg underline" : "text-p-link",
          )}
        >
          {item.action.label}
        </button>
      )}
      {item.dismissible && !item.action && (
        <button
          type="button"
          aria-label="Закрыть"
          onClick={onDismiss}
          className={cn("hit-44 shrink-0", isError ? "text-p-bg" : "text-p-text-muted")}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  return ctx;
}
